import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Dialog from 'components/dialog';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import { Copy } from 'lucide-react';
import Mui from 'materials';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import _ from 'underscore';
import titleCase from 'utils/title-case';
import { useBluViuNutritionState } from 'views/bluviu-nutrition/context/bluviu-nutrition-context';
import { BluViuNutritionSite } from 'views/bluviu-nutrition/types/bluviu-nutrition-site.type';
import { useSelectedSite } from '../../../hooks/use-selected-site';
import { getScenarios } from '../api/bluviu-nutrition';
import { useCreateScenario } from '../hooks/use-create-scenario';
import { CloneScenarioRequest } from '../types/clone-scenario-request.type';
import { ScenarioListItem } from '../types/scenario-list-item.type';

type Props = {
  scenario: ScenarioListItem;
  button?: React.ReactElement;
};

/**
 * TODO: Placeholder for eventual clone scenario dialog. Similar to Clone Scenario Dialog, but has site select
 * @param param0
 * @returns
 */
const CloneScenarioDialog: React.FC<Props> = ({ scenario, button }) => {
  const intl = useIntl();
  const { cloneScenario } = useCreateScenario();
  const {
    account: { company },
  } = useAccountState();
  const {
    bluViuNutritionSitesQuery: { data: sites, isLoading },
  } = useBluViuNutritionState();
  const { selectedSite } = useSelectedSite();
  const history = useHistory();

  const [site, setSite] = useState<BluViuNutritionSite>();
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const defaultValues = useMemo(
    () => ({
      name: '',
      globalSiteId: scenario.globalSiteId,
    }),
    [scenario]
  );

  const { control, reset, handleSubmit } = useForm<CloneScenarioRequest>({
    defaultValues,
  });

  const watchGlobalSiteId = useWatch({
    control,
    name: 'globalSiteId',
  });

  const { data: scenarios, isLoading: isScenariosLoading } = useGet(['getScenarios', watchGlobalSiteId], ({ queryKey }) =>
    getScenarios(String(queryKey[1]))
  );

  // get scenario names to check for invalid names
  const scenarioNames = useMemo(() => scenarios?.map((scenario) => scenario.name), [scenarios]);

  const onSubmit = (data: CloneScenarioRequest) => {
    cloneScenario.mutate(
      { globalCompanyId: company!.companyId, scenarioId: scenario.id, request: data },
      {
        onSuccess: (scenario) => {
          reset();
          dialogRef.current?.setOpen(false);
          history.push(`/bluviu-nutrition/scenarios/${site?.globalSiteId}/${scenario.id}`);
        },
      }
    );
  };

  useEffect(() => {
    const site = sites?.find((site) => site.globalSiteId === watchGlobalSiteId);
    setSite(site);
  }, [watchGlobalSiteId]);

  return (
    <Dialog
      ref={dialogRef}
      maxWidth={'xs'}
      title={`Clone Scenario (${scenario.name})`}
      onClose={() => reset()}
      button={
        button || (
          <Mui.Tooltip title={'Clone'}>
            <Mui.IconButton>
              <Copy size={22} />
            </Mui.IconButton>
          </Mui.Tooltip>
        )
      }>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography fontSize={14}>From site: {selectedSite?.name}</Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <TextField
              dark
              fullWidth
              controller={{
                control,
                name: 'name',
                rules: {
                  required: true,
                  validate: (value) =>
                    !scenarioNames?.includes(value) ? true : `Name must not match an existing scenario within the selected site.`,
                },
              }}
              label={'Scenario name'}
            />
            <TextField fullWidth label={'Scenario Model'} disabled dark value={titleCase(scenario.model)} />
            <Select
              dark
              fullWidth
              label={'Site'}
              isLoading={isLoading}
              controller={{
                control,
                name: 'globalSiteId',
              }}>
              {_.sortBy(sites || [], 'siteName')?.map((site, idx) => (
                <RadioSelectItem key={idx} value={site.globalSiteId}>
                  {site.siteName}
                </RadioSelectItem>
              ))}
            </Select>
            <Box display={'flex'} justifyContent={'end'}>
              <Button
                type="submit"
                disabled={cloneScenario.isPending}
                startIcon={cloneScenario.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
                Clone
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Dialog>
  );
};

export default React.memo(CloneScenarioDialog);
